import { ROUTER_API } from 'app/constants/api.constant';
import { TagFormItemInDTO, TagItemInDTO, TagModelOutDTO } from 'app/fuse-layouts/layout1/type';
import { ListObject } from 'app/main/design-system/models/listObjectsModel';
import BaseApi from 'app/services/api/base.api';
import jwtService from 'app/services/jwtService';
import axios from 'axios';
import { StoreDTO } from '../../stores/type';
import {
  AccessDTO,
  ApiKeyDTO,
  ClientQualificationDTO,
  ConnectFranchiseDTO,
  FranchiseDetails,
  FranchiseInDTO,
  FranchiseSmsPriceDTO,
  FranchiseUpdateOutDTO,
  FranchiseWithStoresDTO,
  ReachableFranchiseDTO,
  ThemeDTO,
} from '../type';

export const franchiseApi = new BaseApi({ baseURL: ROUTER_API, tokenKey: 'token' });

export const createFranchiseApi = async (franchise: Partial<FranchiseWithStoresDTO>) => {
  const { data } = await franchiseApi.api.post<FranchiseWithStoresDTO>(`/api/v2/franchises`, franchise);
  return data;
};

export const getFranchisesApi = async () => {
  const userId = jwtService.getUserId();
  const { data } = await axios.get<FranchiseWithStoresDTO[]>(`api/users/${userId}/franchisesOwned`, {
    params: {
      filter: {
        where: {
          isOfficial: true,
        },
        include: 'storeList',
      },
    },
  });
  return data || [];
};

export const getFranchiseWithStoresApi = async (userId: string) => {
  const { data } = await franchiseApi.api.get<FranchiseWithStoresDTO[]>(`/api/v2/users/${userId}/franchises/stores`);
  return data;
};

export const getUserFranchiseApi = async () => {
  const { data } = await franchiseApi.api.get<FranchiseDetails>('/api/v2/franchises/owned');
  return data;
};

export const getReachableFranchisesApi = async () => {
  const { data } = await franchiseApi.api.get<ReachableFranchiseDTO[]>('/api/v2/franchises/reachable');
  return data;
};

export const connectToFranchiseApi = async (franchiseId: string) => {
  const { data } = await franchiseApi.api.post<ConnectFranchiseDTO>('/api/v2/token/connect-franchise', {
    franchiseId,
  });
  return data;
};

export const associateStoreToFranchisesApi = async (storeListId: string, storeId: string) => {
  const { data } = await axios.put<StoreDTO>(`/api/StoreLists/${storeListId}/stores/rel/${storeId}`);
  return data;
};

export const createLostCustomerMarketingApi = async (franchiseId: string, timeSinceLastVisit: string) => {
  return axios.post<ClientQualificationDTO>(`api/franchises/${franchiseId}/clientQualification`, {
    lostCustomers: {
      timeSinceLastVisit,
    },
  });
};

export const updateLostCustomerMarketingApi = async (franchiseId: string, timeSinceLastVisit: string) => {
  return axios.put<ClientQualificationDTO>(`api/franchises/${franchiseId}/clientQualification`, {
    lostCustomers: {
      timeSinceLastVisit,
    },
  });
};

export const updateFranchiseApi = async (franchiseDetails: FranchiseDetails, updateData: FranchiseUpdateOutDTO) => {
  return franchiseApi.api.patch<FranchiseInDTO>(`api/v2/franchises/${franchiseDetails.id}`, updateData);
};

export const updateFranchiseAverageBasketApi = async (franchiseDetails: FranchiseDetails, averageBasket: number) => {
  return franchiseApi.api.patch<FranchiseDetails>(`api/v2/franchises/${franchiseDetails.id}`, {
    averageBasket,
  });
};

export const checkSenderIdValidityApi = async (senderId: string) => {
  return franchiseApi.api.get<boolean>(`api/v2/franchises/sender-validity`, {
    params: {
      value: senderId,
    },
  });
};

export const getFranchiseTagsApi = async (franchiseId: string, storeId?: string, includeDisabled?: boolean) => {
  try {
    const response = await franchiseApi.api.get<ListObject<TagItemInDTO>>(`api/v2/franchises/${franchiseId}/tags`, {
      params: {
        storeId,
        includeDisabled,
      },
    });
    return response.data;
  } catch (e: any) {
    if (e.response.data.error.status === 'TAGS_NOT_FOUND') {
      return { data: [], metadata: { page: 1, size: 0, total: 0 } };
    }
    throw e;
  }
};

export const patchTagApi = async (tagId: string, data: TagFormItemInDTO) =>
  franchiseApi.api.patch<TagModelOutDTO>(`api/v2/tags/${tagId}`, data);

export const patchThemeApi = async (franchiseId: string, theme: ThemeDTO) =>
  franchiseApi.api.patch<FranchiseDetails>(`api/v2/franchises/${franchiseId}/theme`, theme);

export const getFranchiseApiKeyV1Api = async (franchiseId: string) =>
  franchiseApi.api.get<ApiKeyDTO>(`api/v2/franchises/${franchiseId}/api-key/v1`);

export const getFranchiseApiKeyV2Api = async (franchiseId: string) =>
  franchiseApi.api.get<ApiKeyDTO>(`api/v2/franchises/${franchiseId}/api-key/v2`);

export const getFranchiseDailyAppsKeyApi = async (franchiseId: string) =>
  franchiseApi.api.get<ApiKeyDTO | null>(`api/v2/franchises/${franchiseId}/api-key/dailyapps`);

export const generateFranchiseDailyAppsKeyApi = async (franchiseId: string) =>
  franchiseApi.api.post<ApiKeyDTO>(`api/v2/franchises/${franchiseId}/api-key/dailyapps`);

export const revokeFranchiseDailyAppsKeyApi = async (franchiseId: string) =>
  franchiseApi.api.delete(`api/v2/franchises/${franchiseId}/api-key/dailyapps`);

export const createFranchiseApiKeyV1Api = async (userId: string, accesses: AccessDTO[]) =>
  franchiseApi.api.post<ApiKeyDTO>(`api/v2/api-keys/${userId}`, accesses);

export const createFranchiseApiKeyV2Api = async (franchiseId: string, userId: string) =>
  franchiseApi.api.post<string>('api/v2/api-keys/generate', { franchiseId, userId });

export const getFranchiseSmsPriceApi = async (franchiseId: string) => {
  const response = await franchiseApi.api.get<FranchiseSmsPriceDTO>(`services/v2/franchises/${franchiseId}/sms-price`);
  return response.data;
};
